import registerService from '../services/registerService';
import relationshipService, {
  createRelationshipRequestKey,
  RELATIONSHIP_PREVIEW_MODES,
  toRelationshipProblem,
} from '../services/registerRelationshipService';
import {
  RELATIONSHIP_ATTENTION_CLASSIFICATION,
  RELATIONSHIP_RUN_TERMINAL_STATUSES,
} from '../config/relationshipUi';

const RUN_STATUS_ORDER = Object.freeze({
  awaitingConfirmation: 0,
  queued: 1,
  running: 2,
});

const RUN_STATUS_ORDER_UNKNOWN = -1;
const RUN_STATUS_ORDER_TERMINAL = 3;

const runStatusOrder = (status) => {
  if (RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(status)) return RUN_STATUS_ORDER_TERMINAL;
  return RUN_STATUS_ORDER[status] ?? RUN_STATUS_ORDER_UNKNOWN;
};

const acceptsRunUpdate = (currentRun, nextRun) => {
  if (!currentRun || !nextRun || currentRun.id !== nextRun.id) return true;
  const currentOrder = runStatusOrder(currentRun.status);
  const nextOrder = runStatusOrder(nextRun.status);
  // A status this client does not know cannot be ordered against a known one. Accepting the
  // server value keeps it authoritative; rejecting it would freeze the run view on a stale
  // snapshot with no way to recover through refresh.
  if (currentOrder === RUN_STATUS_ORDER_UNKNOWN || nextOrder === RUN_STATUS_ORDER_UNKNOWN) return true;
  return nextOrder >= currentOrder;
};

const emptyAccess = () => ({
  canRead: false,
  canDeclare: false,
  canEdit: false,
  canEnable: false,
  canPreview: false,
  canConfirm: false,
  canReconcile: false,
  canDelete: false,
  canManageSchema: false,
  deleteApprovalPurpose: null,
});

// Three different truths the workspace must not blur together: a denial the user could fix by
// asking for access, a capability this deployment does not expose, and a request that failed.
const availabilityForProblem = (problem) => {
  if (problem?.status === 403) return 'forbidden';
  if (problem?.status === 404 || problem?.status === 501) return 'unavailable';
  return 'error';
};

const contractProblem = (detail, path = '') => ({
  type: null,
  status: null,
  code: 'RELATIONSHIP_RESPONSE_INVALID',
  title: 'Relationship response is invalid',
  detail,
  traceId: null,
  errors: path ? [{ path, code: 'invalidResponseShape', message: detail }] : [],
});

const catalogItems = (payload) => {
  if (!payload || !Array.isArray(payload.items)) {
    throw contractProblem('The capability catalog did not return its required items array.', '/items');
  }
  return payload.items;
};

const cursorPage = (payload, label) => {
  if (!payload || !Array.isArray(payload.items)) {
    throw contractProblem(`${label} did not return its required items array.`, '/items');
  }
  if (!Number.isInteger(payload.totalCount) || payload.totalCount < 0) {
    throw contractProblem(`${label} did not return a valid totalCount.`, '/totalCount');
  }
  if (!Object.prototype.hasOwnProperty.call(payload, 'nextCursor')
    || (payload.nextCursor !== null && typeof payload.nextCursor !== 'string')) {
    throw contractProblem(`${label} returned an invalid nextCursor.`, '/nextCursor');
  }

  return {
    items: payload.items,
    nextCursor: payload.nextCursor || null,
    totalCount: payload.totalCount,
  };
};

const persistedRunItemPage = (payload, label) => {
  const page = cursorPage(payload, label);
  return {
    ...page,
    items: page.items.map((item) => ({
      ...item,
      _sourceValueReadState: item?.confirmBlockedCode === 'FACILITY_VIEW_REQUIRED'
        ? 'redacted'
        : 'unavailable',
    })),
  };
};

const permissionsFrom = (payload) => {
  if (!payload?.effectivePermissions || typeof payload.effectivePermissions !== 'object') {
    throw contractProblem(
      'The relationship list did not return effectivePermissions.',
      '/effectivePermissions',
    );
  }
  return {
    ...emptyAccess(),
    ...payload.effectivePermissions,
  };
};

const requireGroupScopeFingerprints = (items) => {
  const missingIndex = items.findIndex((item) => item?.targetScopeKind === 'facilityGroup'
    && !(typeof item?.groupScopeFingerprint === 'string' && item.groupScopeFingerprint.trim()));
  if (missingIndex >= 0) {
    throw contractProblem(
      'A Facility Group plan item is missing its approved membership fingerprint.',
      `/plan/items/${missingIndex}/groupScopeFingerprint`,
    );
  }

  const unexpectedIndex = items.findIndex((item) => item?.targetScopeKind === 'facility'
    && item?.groupScopeFingerprint !== null && item?.groupScopeFingerprint !== undefined);
  if (unexpectedIndex >= 0) {
    throw contractProblem(
      'A facility-scoped plan item returned an unexpected group fingerprint.',
      `/plan/items/${unexpectedIndex}/groupScopeFingerprint`,
    );
  }
};

const runItemKey = (item) => item?.planItemKey;

const approvalTokenFrom = (grant) => {
  if (typeof grant?.approvalGrantToken === 'string' && grant.approvalGrantToken.trim()) {
    return grant.approvalGrantToken;
  }
  throw contractProblem('The server did not return approvalGrantToken.', '/approvalGrantToken');
};

const relationshipEtag = (relationship) => {
  if (typeof relationship?.etag === 'string' && relationship.etag.trim()) return relationship.etag;
  throw contractProblem(
    'This relationship has no authoritative ETag. Refresh the relationship list before changing it.',
    '/etag',
  );
};

/**
 * How the server refuses to enable a relationship whose saved configuration predates the fixed
 * automatic contract (409, `RelationshipHttpDeclarationCommandService`).
 *
 * The server is the only authority on this: its acceptance test is looser than the patch builder
 * below, so asking the question from a client-side reading would prompt for upgrades the server
 * does not want. The workspace waits for this code, asks the user, and only then upgrades.
 */
export const RELATIONSHIP_AUTOMATIC_CONTRACT_UPGRADE_REQUIRED = 'RELATIONSHIP_AUTOMATIC_CONTRACT_UPGRADE_REQUIRED';

/**
 * The patch that brings a legacy relationship onto the fixed automatic contract, or null when this
 * snapshot already carries it. Only ever built after the user has agreed to the upgrade.
 */
const fixedAutomaticContractPatch = (relationship) => {
  if (!Array.isArray(relationship?.mapping)) {
    throw contractProblem(
      'This relationship has no authoritative mapping. Refresh the relationship list before enabling it.',
      '/mapping',
    );
  }

  const mappingIsFixed = relationship.mapping.every((entry) => {
    const transform = entry?.transform;
    return entry?.direction === 'both'
      && entry?.onExisting === 'ignore'
      && ['identity', 'trim'].includes(transform?.kind)
      && (transform.arguments === null || transform.arguments === undefined);
  });
  if (relationship.syncMode === 'bidirectional'
    && relationship.runOnImport === true
    && mappingIsFixed) return null;

  return {
    mapping: relationship.mapping.map((entry) => ({
      sourceFieldKey: entry?.sourceFieldKey,
      destination: entry?.destination,
      onCreate: entry?.onCreate,
      onExisting: 'ignore',
      direction: 'both',
      transform: {
        kind: entry?.transform?.kind === 'none' ? 'trim' : entry?.transform?.kind,
        arguments: null,
      },
      onError: entry?.onError,
    })),
    syncMode: 'bidirectional',
    runOnImport: true,
  };
};

// Covers every RelationshipRunScopeKind the server can serialize. An unmapped kind stays null so
// confirmation fails closed rather than requesting a grant the server would reject.
const RUN_SCOPE_APPROVAL_PURPOSES = Object.freeze({
  rows: 'confirmRun',
  register: 'confirmRun',
  filtered: 'confirmRun',
  import: 'confirmRun',
  reconcile: 'confirmRun',
  cleanup: 'confirmCleanup',
});

const approvalPurposeForRun = (run) => RUN_SCOPE_APPROVAL_PURPOSES[run?.scope?.kind] || null;

const assertConfirmablePlan = (state) => {
  const run = state.currentRun;
  if (typeof run?.confirmBlocked !== 'boolean' || typeof run?.targetRedacted !== 'boolean') {
    throw contractProblem('The run is missing its redaction state.', '/confirmBlocked');
  }
  if (run?.confirmBlocked === true || run?.targetRedacted === true) {
    throw contractProblem(
      run?.confirmBlockedCode || 'This plan is redacted and cannot be confirmed.',
      '/confirmBlocked',
    );
  }
  if (state.currentRunItemsCursor) {
    throw contractProblem('Load every persisted plan item before confirming.', '/plan/nextCursor');
  }
  if (!Number.isInteger(state.currentRunItemsTotal)
    || state.currentRunItems.length !== state.currentRunItemsTotal) {
    throw contractProblem('The complete persisted plan is required before confirmation.', '/plan/totalCount');
  }

  state.currentRunItems.forEach((item, index) => {
    if (!Number.isInteger(item?.planItemKey) || item.planItemKey < 1) {
      throw contractProblem('A persisted plan item is missing planItemKey.', `/plan/items/${index}/planItemKey`);
    }
    if (!['action', 'outcome'].includes(item?.itemKind)) {
      throw contractProblem('A persisted plan item has an unknown itemKind.', `/plan/items/${index}/itemKind`);
    }
    if (!item.rowSnapshot || typeof item.rowSnapshot !== 'object'
      || typeof item.rowSnapshot.rowId !== 'string'
      || typeof item.rowSnapshot.sourceFieldKey !== 'string'
      || typeof item.rowSnapshot.sourceFingerprint !== 'string'
      || !Object.prototype.hasOwnProperty.call(item.rowSnapshot, 'sourceValue')) {
      throw contractProblem('A persisted plan item is missing rowSnapshot.', `/plan/items/${index}/rowSnapshot`);
    }
    if (!Number.isInteger(item.candidateCount) || item.candidateCount < 0) {
      throw contractProblem('A persisted plan item has an invalid candidateCount.', `/plan/items/${index}/candidateCount`);
    }
    if (typeof item.targetRedacted !== 'boolean' || typeof item.confirmBlocked !== 'boolean') {
      throw contractProblem('A persisted plan item is missing its redaction state.', `/plan/items/${index}`);
    }
    if (!['facility', 'facilityGroup'].includes(item.targetScopeKind)
      || typeof item.targetScopeId !== 'string') {
      throw contractProblem('A persisted plan item is missing its target scope.', `/plan/items/${index}`);
    }
    if (item.targetRedacted === true && item.candidateCount !== 0) {
      throw contractProblem('A redacted item leaked its candidate count.', `/plan/items/${index}/candidateCount`);
    }
    if (item.itemKind === 'action' && (item.targetRedacted === true || item.confirmBlocked === true)) {
      throw contractProblem(
        item.confirmBlockedCode || 'A redacted action cannot be confirmed.',
        `/plan/items/${index}/confirmBlocked`,
      );
    }
    if (item.itemKind === 'action') {
      const actionShapeValid = ['create', 'link', 'syncFields', 'repoint', 'cleanup'].includes(item.action)
        && item.preconditions && typeof item.preconditions === 'object'
        && item.actionPayload && Array.isArray(item.actionPayload.destinations)
        && Array.isArray(item.fieldOutcomes)
        && typeof item.actionPayloadHash === 'string'
        && ['grant', 'none'].includes(item.mapAuthorityDisposition);
      if (!actionShapeValid) {
        throw contractProblem('A persisted action is missing PlanHash-bound facts.', `/plan/items/${index}`);
      }
      if (['syncFields', 'repoint', 'cleanup'].includes(item.action)) {
        const expectedLink = item.expectedLink;
        if (!expectedLink || typeof expectedLink.rowVersion !== 'string'
          || typeof expectedLink.associationState !== 'string'
          || typeof expectedLink.oldTargetIdentityHash !== 'string') {
          throw contractProblem('A persisted action is missing its expected-link CAS triple.', `/plan/items/${index}/expectedLink`);
        }
      }
      if (item.action === 'syncFields'
        || (state.currentRunMode === RELATIONSHIP_PREVIEW_MODES.SYNC_FIELDS && item.action === 'link')) {
        const missingCurrentValue = item.actionPayload.destinations.findIndex(
          destination => !destination || !Object.prototype.hasOwnProperty.call(destination, 'currentValue'),
        );
        if (missingCurrentValue >= 0) {
          throw contractProblem(
            'A field-sync action is missing the current target value.',
            `/plan/items/${index}/actionPayload/destinations/${missingCurrentValue}/currentValue`,
          );
        }
      }
    } else if (typeof item.outcomeCode !== 'string' || !item.outcomeCode.trim()
      || !item.reason || typeof item.reason.code !== 'string'
      || !item.reason.data || typeof item.reason.data !== 'object') {
      throw contractProblem('A persisted outcome is missing its code or structured reason.', `/plan/items/${index}/outcomeCode`);
    }
  });
};

let runRequestSequence = 0;
let workspaceRequestSequence = 0;
let schemaRequestSequence = 0;

export default {
  namespaced: true,

  state: {
    registerId: null,
    availability: 'unknown',
    capabilities: [],
    relationships: [],
    relationshipsCursor: null,
    relationshipsTotal: null,
    loadingMoreRelationships: false,
    validatingRelationshipIds: [],
    registerSchema: null,
    destinationSchema: null,
    access: emptyAccess(),
    runHistory: [],
    historyAvailability: 'unknown',
    historyProblem: null,
    runHistoryCursor: null,
    runHistoryTotal: null,
    loadingRunHistory: false,
    runHistoryReloadPending: false,
    currentRun: null,
    currentRunMode: RELATIONSHIP_PREVIEW_MODES.RESOLVE,
    currentRunApprovalPurpose: null,
    currentRunItems: [],
    currentRunItemsCursor: null,
    currentRunItemsTotal: null,
    runRequestId: 0,
    workspaceRequestId: 0,
    schemaRequestId: 0,
    loadingRunItems: false,
    loading: false,
    saving: false,
    previewing: false,
    confirming: false,
    cancelling: false,
    problem: null,
  },

  getters: {
    isReady: (state) => state.availability === 'ready',
    activeCapabilities: (state) => state.capabilities.filter((item) => item?.isEnabled === true),
    relationshipById: (state) => (id) => state.relationships.find((item) => item.id === id),
  },

  mutations: {
    RESET_WORKSPACE(state, registerId) {
      state.registerId = registerId;
      state.availability = 'unknown';
      state.capabilities = [];
      state.relationships = [];
      state.relationshipsCursor = null;
      state.relationshipsTotal = null;
      state.loadingMoreRelationships = false;
      state.validatingRelationshipIds = [];
      state.registerSchema = null;
      state.destinationSchema = null;
      state.access = emptyAccess();
      state.runHistory = [];
      state.historyAvailability = 'unknown';
      state.historyProblem = null;
      state.runHistoryCursor = null;
      state.runHistoryTotal = null;
      state.loadingRunHistory = false;
      state.runHistoryReloadPending = false;
      state.currentRun = null;
      state.currentRunMode = RELATIONSHIP_PREVIEW_MODES.RESOLVE;
      state.currentRunApprovalPurpose = null;
      state.currentRunItems = [];
      state.currentRunItemsCursor = null;
      state.currentRunItemsTotal = null;
      state.runRequestId = ++runRequestSequence;
      state.workspaceRequestId = ++workspaceRequestSequence;
      state.schemaRequestId = ++schemaRequestSequence;
      state.loadingRunItems = false;
      state.problem = null;
    },
    SET_AVAILABILITY(state, value) { state.availability = value; },
    SET_CAPABILITIES(state, value) { state.capabilities = value; },
    SET_RELATIONSHIPS(state, { items, cursor, total }) {
      state.relationships = items;
      state.relationshipsCursor = cursor || null;
      state.relationshipsTotal = total;
    },
    APPEND_RELATIONSHIPS(state, { items, cursor, total }) {
      const merged = new Map(state.relationships.map((relationship) => [relationship.id, relationship]));
      items.forEach((relationship) => merged.set(relationship.id, relationship));
      state.relationships = Array.from(merged.values());
      state.relationshipsCursor = cursor || null;
      state.relationshipsTotal = total;
    },
    SET_REGISTER_SCHEMA(state, value) { state.registerSchema = value; },
    SET_DESTINATION_SCHEMA(state, value) { state.destinationSchema = value; },
    SET_ACCESS(state, value) { state.access = value; },
    SET_HISTORY_AVAILABILITY(state, value) { state.historyAvailability = value; },
    SET_HISTORY_PROBLEM(state, value) { state.historyProblem = value; },
    SET_RUN_HISTORY(state, { items, cursor, total = null }) {
      state.runHistory = items;
      state.runHistoryCursor = cursor || null;
      state.runHistoryTotal = total;
    },
    APPEND_RUN_HISTORY(state, { items, cursor, total = null }) {
      const merged = new Map(state.runHistory.map((run) => [run.id, run]));
      items.forEach((run) => merged.set(run.id, run));
      state.runHistory = Array.from(merged.values());
      state.runHistoryCursor = cursor || null;
      if (total !== null) state.runHistoryTotal = total;
    },
    SET_CURRENT_RUN(state, value) {
      if (value === null || acceptsRunUpdate(state.currentRun, value)) state.currentRun = value;
    },
    SET_CURRENT_RUN_MODE(state, value) { state.currentRunMode = value; },
    SET_CURRENT_RUN_APPROVAL_PURPOSE(state, value) { state.currentRunApprovalPurpose = value; },
    SET_CURRENT_RUN_ITEMS(state, { items, cursor, total = null }) {
      state.currentRunItems = items;
      state.currentRunItemsCursor = cursor || null;
      state.currentRunItemsTotal = total;
    },
    APPEND_CURRENT_RUN_ITEMS(state, { items, cursor, total = null }) {
      const merged = new Map(state.currentRunItems.map((item) => [runItemKey(item), item]));
      items.forEach((item) => merged.set(runItemKey(item), item));
      state.currentRunItems = Array.from(merged.values());
      state.currentRunItemsCursor = cursor || null;
      if (total !== null) state.currentRunItemsTotal = total;
    },
    SET_RUN_REQUEST_ID(state, value) { state.runRequestId = value; },
    SET_WORKSPACE_REQUEST_ID(state, value) { state.workspaceRequestId = value; },
    SET_SCHEMA_REQUEST_ID(state, value) { state.schemaRequestId = value; },
    SET_LOADING_RUN_ITEMS(state, value) { state.loadingRunItems = value; },
    SET_LOADING_MORE_RELATIONSHIPS(state, value) { state.loadingMoreRelationships = value; },
    CLEAR_RELATIONSHIP_VALIDATIONS(state) { state.validatingRelationshipIds = []; },
    SET_RELATIONSHIP_VALIDATING(state, { relationshipId, validating }) {
      const ids = new Set(state.validatingRelationshipIds);
      if (validating) ids.add(relationshipId);
      else ids.delete(relationshipId);
      state.validatingRelationshipIds = Array.from(ids);
    },
    SET_LOADING_RUN_HISTORY(state, value) { state.loadingRunHistory = value; },
    SET_RUN_HISTORY_RELOAD_PENDING(state, value) { state.runHistoryReloadPending = value; },
    SET_LOADING(state, value) { state.loading = value; },
    SET_SAVING(state, value) { state.saving = value; },
    SET_PREVIEWING(state, value) { state.previewing = value; },
    SET_CONFIRMING(state, value) { state.confirming = value; },
    SET_CANCELLING(state, value) { state.cancelling = value; },
    SET_PROBLEM(state, value) { state.problem = value; },
  },

  actions: {
    INVALIDATE_DESTINATION_SCHEMA_REQUEST({ commit, state }, expectedRequestId) {
      if (!Number.isInteger(expectedRequestId)
        || state.schemaRequestId !== expectedRequestId) return false;
      const requestId = ++schemaRequestSequence;
      commit('SET_SCHEMA_REQUEST_ID', requestId);
      return true;
    },

    async LOAD_WORKSPACE({ commit, dispatch, state }, registerId) {
      if (!registerId) return;
      if (state.registerId !== registerId) commit('RESET_WORKSPACE', registerId);

      const requestId = ++workspaceRequestSequence;
      commit('SET_WORKSPACE_REQUEST_ID', requestId);
      commit('CLEAR_RELATIONSHIP_VALIDATIONS');

      commit('SET_LOADING', true);
      commit('SET_PROBLEM', null);
      commit('SET_LOADING_RUN_HISTORY', false);
      dispatch('LOAD_RUN_HISTORY', { reset: true }).catch(() => {
        // History has its own availability/problem state and never invalidates core reads.
      });

      try {
        const [catalogPayload, relationshipPayload] = await Promise.all([
          relationshipService.getCapabilities(),
          relationshipService.listRelationships(registerId),
        ]);

        if (state.workspaceRequestId !== requestId || state.registerId !== registerId) return;

        const relationshipPage = cursorPage(relationshipPayload, 'The relationship list');
        commit('SET_CAPABILITIES', catalogItems(catalogPayload));
        commit('SET_RELATIONSHIPS', {
          items: relationshipPage.items,
          cursor: relationshipPage.nextCursor,
          total: relationshipPage.totalCount,
        });
        commit('SET_ACCESS', permissionsFrom(relationshipPayload));
        commit('SET_AVAILABILITY', 'ready');

        try {
          const schemaPayload = await registerService.getRegisterSchema(registerId);
          if (state.workspaceRequestId === requestId && state.registerId === registerId) {
            commit('SET_REGISTER_SCHEMA', schemaPayload);
          }
        } catch (schemaErr) {
          // Schema is an editor dependency, not a prerequisite for the read-only workspace.
        }
      } catch (err) {
        const problem = toRelationshipProblem(err, 'Relationships could not be loaded.');
        if (state.workspaceRequestId !== requestId || state.registerId !== registerId) return;
        commit('SET_AVAILABILITY', availabilityForProblem(problem));
        commit('SET_PROBLEM', problem);
      } finally {
        if (state.workspaceRequestId === requestId && state.registerId === registerId) {
          commit('SET_LOADING', false);
        }
      }
    },

    async REFRESH_RELATIONSHIPS({ commit, state }) {
      if (!state.registerId) return;
      const registerId = state.registerId;
      const requestId = state.workspaceRequestId;
      const loadedTarget = Math.max(state.relationships.length, 1);
      commit('SET_PROBLEM', null);
      try {
        const payload = await relationshipService.listRelationships(registerId);
        if (state.registerId !== registerId || state.workspaceRequestId !== requestId) return;
        const firstPage = cursorPage(payload, 'The relationship list');
        const items = [...firstPage.items];
        let cursor = firstPage.nextCursor;
        while (cursor && items.length < loadedTarget) {
          const nextPayload = await relationshipService.listRelationships(registerId, { cursor });
          if (state.registerId !== registerId || state.workspaceRequestId !== requestId) return;
          const nextPage = cursorPage(nextPayload, 'The relationship list');
          items.push(...nextPage.items);
          cursor = nextPage.nextCursor;
        }
        commit('SET_RELATIONSHIPS', {
          items,
          cursor,
          total: firstPage.totalCount,
        });
        commit('SET_ACCESS', permissionsFrom(payload));
      } catch (err) {
        const problem = toRelationshipProblem(err, 'Relationship status could not be refreshed.');
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_PROBLEM', problem);
        }
        throw problem;
      }
    },

    async LOAD_MORE_RELATIONSHIPS({ commit, state }) {
      if (!state.registerId || !state.relationshipsCursor || state.loadingMoreRelationships) return null;
      const registerId = state.registerId;
      const requestId = state.workspaceRequestId;
      const cursor = state.relationshipsCursor;
      commit('SET_LOADING_MORE_RELATIONSHIPS', true);
      try {
        const payload = await relationshipService.listRelationships(registerId, { cursor });
        if (state.registerId !== registerId || state.workspaceRequestId !== requestId) return null;
        const page = cursorPage(payload, 'The relationship list');
        commit('APPEND_RELATIONSHIPS', {
          items: page.items,
          cursor: page.nextCursor,
          total: page.totalCount,
        });
        return payload;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'More relationships could not be loaded.');
        if (state.registerId === registerId && state.workspaceRequestId === requestId) commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_LOADING_MORE_RELATIONSHIPS', false);
        }
      }
    },

    async LOAD_RUN_HISTORY({ commit, dispatch, state }, { reset = true, pageSize = null, force = false } = {}) {
      if (!state.registerId) return null;
      if (state.loadingRunHistory) {
        if (force) commit('SET_RUN_HISTORY_RELOAD_PENDING', true);
        return null;
      }
      const registerId = state.registerId;
      const requestId = state.workspaceRequestId;
      commit('SET_RUN_HISTORY_RELOAD_PENDING', false);
      commit('SET_LOADING_RUN_HISTORY', true);
      commit('SET_HISTORY_AVAILABILITY', 'loading');
      commit('SET_HISTORY_PROBLEM', null);
      if (reset) commit('SET_RUN_HISTORY', { items: [], cursor: null, total: null });
      try {
        const payload = await relationshipService.getRunHistory(registerId, { pageSize });
        if (state.registerId !== registerId || state.workspaceRequestId !== requestId) return null;
        const page = cursorPage(payload, 'The run history');
        commit('SET_RUN_HISTORY', {
          items: page.items,
          cursor: page.nextCursor,
          total: page.totalCount,
        });
        commit('SET_HISTORY_AVAILABILITY', 'ready');
        return payload;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'Run history could not be loaded.');
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_HISTORY_AVAILABILITY', availabilityForProblem(problem));
          commit('SET_HISTORY_PROBLEM', problem);
        }
        throw problem;
      } finally {
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_LOADING_RUN_HISTORY', false);
          if (state.runHistoryReloadPending) {
            commit('SET_RUN_HISTORY_RELOAD_PENDING', false);
            dispatch('LOAD_RUN_HISTORY', { reset: true, force: true }).catch(() => {
              // The history panel keeps the typed retry state from the follow-up request.
            });
          }
        }
      }
    },

    async LOAD_MORE_RUN_HISTORY({ commit, dispatch, state }) {
      if (state.historyAvailability !== 'ready'
        || !state.registerId
        || !state.runHistoryCursor
        || state.loadingRunHistory) return null;
      const registerId = state.registerId;
      const requestId = state.workspaceRequestId;
      const cursor = state.runHistoryCursor;
      commit('SET_LOADING_RUN_HISTORY', true);
      commit('SET_HISTORY_PROBLEM', null);
      try {
        const payload = await relationshipService.getRunHistory(registerId, { cursor });
        if (state.registerId !== registerId || state.workspaceRequestId !== requestId) return null;
        const page = cursorPage(payload, 'The run history');
        commit('APPEND_RUN_HISTORY', {
          items: page.items,
          cursor: page.nextCursor,
          total: page.totalCount,
        });
        return payload;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'More run history could not be loaded.');
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_HISTORY_PROBLEM', problem);
        }
        throw problem;
      } finally {
        if (state.registerId === registerId && state.workspaceRequestId === requestId) {
          commit('SET_LOADING_RUN_HISTORY', false);
          if (state.runHistoryReloadPending) {
            commit('SET_RUN_HISTORY_RELOAD_PENDING', false);
            dispatch('LOAD_RUN_HISTORY', { reset: true, force: true }).catch(() => {
              // The history panel keeps the typed retry state from the follow-up request.
            });
          }
        }
      }
    },

    async RESOLVE_DESTINATION_SCHEMA({ commit, state }, { targetKind, targetContext = null }) {
      const requestId = ++schemaRequestSequence;
      const registerId = state.registerId;
      const workspaceRequestId = state.workspaceRequestId;
      commit('SET_SCHEMA_REQUEST_ID', requestId);
      commit('SET_DESTINATION_SCHEMA', null);
      commit('SET_PROBLEM', null);
      try {
        const result = await relationshipService.resolveTargetSchema(
          state.registerId,
          targetKind,
          targetContext,
        );
        if (state.schemaRequestId !== requestId
          || state.workspaceRequestId !== workspaceRequestId
          || state.registerId !== registerId) return null;
        commit('SET_DESTINATION_SCHEMA', result);
        return result;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The target field schema could not be resolved.');
        if (state.schemaRequestId === requestId
          && state.workspaceRequestId === workspaceRequestId
          && state.registerId === registerId) {
          commit('SET_PROBLEM', problem);
        }
        throw problem;
      }
    },

    async CREATE_RELATIONSHIP({ commit, dispatch, state }, declaration) {
      commit('SET_SAVING', true);
      commit('SET_PROBLEM', null);
      try {
        const created = await relationshipService.createRelationship(
          state.registerId,
          declaration,
          createRelationshipRequestKey('declare'),
        );
        await dispatch('REFRESH_RELATIONSHIPS');
        return created;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The relationship could not be saved.');
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    async UPDATE_RELATIONSHIP({ commit, dispatch, state }, { relationship, patch }) {
      commit('SET_SAVING', true);
      commit('SET_PROBLEM', null);
      try {
        const updated = await relationshipService.updateRelationship(
          state.registerId,
          relationship.id,
          patch,
          relationshipEtag(relationship),
        );
        await dispatch('REFRESH_RELATIONSHIPS');
        return updated;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The relationship could not be updated.');
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    async VALIDATE_RELATIONSHIP({ commit, state }, relationshipId) {
      if (state.availability !== 'ready'
        || state.access?.canEdit !== true
        || !state.registerId
        || !relationshipId
        || state.validatingRelationshipIds.includes(relationshipId)) return null;

      const registerId = state.registerId;
      const workspaceRequestId = state.workspaceRequestId;
      const isCurrentWorkspace = () => state.registerId === registerId
        && state.workspaceRequestId === workspaceRequestId;
      commit('SET_RELATIONSHIP_VALIDATING', { relationshipId, validating: true });
      commit('SET_PROBLEM', null);
      try {
        await relationshipService.validateRelationship(registerId, relationshipId);
        return isCurrentWorkspace() ? { stale: false } : { stale: true };
      } catch (err) {
        if (!isCurrentWorkspace()) return { stale: true };
        const problem = toRelationshipProblem(err, 'The relationship is not valid.');
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        if (isCurrentWorkspace()) {
          commit('SET_RELATIONSHIP_VALIDATING', { relationshipId, validating: false });
        }
      }
    },

    async SET_RELATIONSHIP_ENABLED(
      { commit, dispatch, state },
      { relationship, enabled, upgradeConfirmed = false },
    ) {
      commit('SET_SAVING', true);
      commit('SET_PROBLEM', null);
      try {
        let commandRelationship = relationship;
        let approvalGrantToken = null;
        if (enabled) {
          // Enabling never rewrites a saved configuration on its own. A legacy relationship is
          // refused by the server, the workspace puts that refusal to the user, and only the
          // dispatch carrying their answer is allowed to upgrade.
          if (upgradeConfirmed) {
            const upgradePatch = fixedAutomaticContractPatch(commandRelationship);
            // The server's contract test is looser than this one, so a snapshot the server refused
            // cannot also satisfy the builder — a null patch here means the snapshot has drifted
            // from the server's copy. Send nothing and let the server's own refusal be the answer,
            // rather than inventing a rewrite of a configuration this client cannot see.
            if (upgradePatch) {
              commandRelationship = await relationshipService.updateRelationship(
                state.registerId,
                commandRelationship.id,
                upgradePatch,
                relationshipEtag(commandRelationship),
              );
              // The upgrade is a complete, safe server mutation on its own. Refresh immediately so
              // a later grant or enable failure never leaves the workspace holding the obsolete
              // ETag.
              await dispatch('REFRESH_RELATIONSHIPS');
            }
          }
          const grant = await relationshipService.createApprovalGrant({
            purpose: 'relationshipConfig',
            registerId: state.registerId,
            relationshipId: commandRelationship.id,
            configVersion: commandRelationship.configVersion,
          });
          approvalGrantToken = approvalTokenFrom(grant);
        }
        const result = await relationshipService.setRelationshipEnabled(
          state.registerId,
          commandRelationship.id,
          enabled,
          relationshipEtag(commandRelationship),
          approvalGrantToken,
          enabled ? createRelationshipRequestKey('enable') : null,
        );
        await dispatch('REFRESH_RELATIONSHIPS');
        return result;
      } catch (err) {
        let problem = toRelationshipProblem(err, `The relationship could not be ${enabled ? 'enabled' : 'disabled'}.`);
        if (problem.code === 'ETAG_MISMATCH') {
          try {
            await dispatch('REFRESH_RELATIONSHIPS');
            problem = {
              ...problem,
              detail: 'The relationship changed while this action was being prepared. Its latest state is now shown; review it and try again.',
            };
          } catch (refreshErr) {
            // REFRESH_RELATIONSHIPS already records its typed read failure. Preserve the original
            // command refusal for the caller without replacing that more actionable workspace state.
            throw problem;
          }
        }
        // The upgrade refusal is a question the workspace is about to ask, not a failure to
        // report. Recording it would leave a stale workspace problem behind the confirmation.
        if (problem.code !== RELATIONSHIP_AUTOMATIC_CONTRACT_UPGRADE_REQUIRED) {
          commit('SET_PROBLEM', problem);
        }
        throw problem;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    async DELETE_RELATIONSHIP({ commit, dispatch, state }, relationship) {
      commit('SET_SAVING', true);
      commit('SET_PROBLEM', null);
      try {
        const deleteApprovalPurpose = state.access?.deleteApprovalPurpose;
        if (deleteApprovalPurpose !== 'relationshipDelete') {
          throw contractProblem(
            'The server did not advertise relationshipDelete. Delete remains unavailable.',
            '/effectivePermissions/deleteApprovalPurpose',
          );
        }
        const grant = await relationshipService.createApprovalGrant({
          purpose: 'relationshipDelete',
          registerId: state.registerId,
          relationshipId: relationship.id,
          configVersion: relationship.configVersion,
        });
        const result = await relationshipService.deleteRelationship(
          state.registerId,
          relationship.id,
          approvalTokenFrom(grant),
          relationshipEtag(relationship),
          createRelationshipRequestKey('delete-relationship'),
        );
        await dispatch('REFRESH_RELATIONSHIPS');
        return result;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The relationship could not be deleted.');
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    async PREVIEW_RUN(
      { commit, state },
      {
        relationshipId,
        scope,
        cleanup = false,
        mode = RELATIONSHIP_PREVIEW_MODES.RESOLVE,
      },
    ) {
      const requestId = ++runRequestSequence;
      const registerId = state.registerId;
      commit('SET_RUN_REQUEST_ID', requestId);
      commit('SET_PREVIEWING', true);
      commit('SET_PROBLEM', null);
      commit('SET_CURRENT_RUN', null);
      commit('SET_CURRENT_RUN_MODE', mode);
      commit('SET_CURRENT_RUN_APPROVAL_PURPOSE', null);
      commit('SET_CURRENT_RUN_ITEMS', { items: [], cursor: null });

      const request = { relationshipId, scope, mode };
      // Every explicit build gets a fresh key. Changing a selection/filter can
      // therefore never replay a persisted preview built for an older scope.
      const requestKey = createRelationshipRequestKey(
        cleanup
          ? 'preview-cleanup'
          : mode === RELATIONSHIP_PREVIEW_MODES.SYNC_FIELDS
            ? 'preview-sync-fields'
            : 'preview',
      );
      try {
        const run = cleanup
          ? await relationshipService.previewCleanup(
            state.registerId,
            request,
            requestKey,
          )
          : await relationshipService.previewRun(
            state.registerId,
            request,
            requestKey,
          );

        if (state.runRequestId !== requestId || state.registerId !== registerId) return null;

        commit('SET_CURRENT_RUN', run);
        commit('SET_CURRENT_RUN_APPROVAL_PURPOSE', cleanup ? 'confirmCleanup' : 'confirmRun');
        const planPage = cursorPage(run?.plan, 'The preview plan');
        commit('SET_CURRENT_RUN_ITEMS', {
          items: planPage.items,
          cursor: planPage.nextCursor,
          total: planPage.totalCount,
        });
        return run;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The preview could not be created.');
        if (state.runRequestId === requestId) commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        if (state.runRequestId === requestId) commit('SET_PREVIEWING', false);
      }
    },

    async LOAD_RUN({ commit, state }, runId) {
      if (!runId) return null;
      const requestId = ++runRequestSequence;
      const registerId = state.registerId;
      commit('SET_RUN_REQUEST_ID', requestId);
      commit('SET_CURRENT_RUN', null);
      commit('SET_CURRENT_RUN_MODE', RELATIONSHIP_PREVIEW_MODES.RESOLVE);
      commit('SET_CURRENT_RUN_APPROVAL_PURPOSE', null);
      commit('SET_CURRENT_RUN_ITEMS', { items: [], cursor: null, total: null });
      commit('SET_LOADING_RUN_ITEMS', true);
      commit('SET_PROBLEM', null);
      try {
        const run = await relationshipService.getRun(runId);
        if (state.runRequestId !== requestId || state.registerId !== registerId) return null;
        commit('SET_CURRENT_RUN', run);
        commit('SET_CURRENT_RUN_APPROVAL_PURPOSE', approvalPurposeForRun(run));

        const itemsPayload = await relationshipService.getRunItems(runId);
        const currentRunId = state.currentRun?.id;
        if (state.runRequestId !== requestId || state.registerId !== registerId || currentRunId !== runId) return null;
        const page = persistedRunItemPage(itemsPayload, 'The run items');
        commit('SET_CURRENT_RUN_ITEMS', {
          items: page.items,
          cursor: page.nextCursor,
          total: page.totalCount,
        });
        return run;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'The persisted run could not be loaded.');
        if (state.runRequestId === requestId && state.registerId === registerId) commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        if (state.runRequestId === requestId && state.registerId === registerId) {
          commit('SET_LOADING_RUN_ITEMS', false);
        }
      }
    },

    async LOAD_RUN_STATUS({ commit, state }, { runId, signal = null, timeout = null } = {}) {
      if (!runId) return null;
      const run = await relationshipService.getRun(runId, { signal, timeout });
      if (signal?.aborted) return null;
      if (state.currentRun?.id === runId) {
        commit('SET_CURRENT_RUN', run);
        return state.currentRun;
      }
      return run;
    },

    async LOAD_MORE_CURRENT_RUN_ITEMS({ commit, state }, { reset = false } = {}) {
      const runId = state.currentRun?.id;
      if (!runId || state.loadingRunItems || (!reset && !state.currentRunItemsCursor)) return null;

      const requestId = state.runRequestId;
      const cursor = reset ? null : state.currentRunItemsCursor;
      commit('SET_LOADING_RUN_ITEMS', true);
      try {
        const payload = await relationshipService.getRunItems(runId, { cursor });
        const currentRunId = state.currentRun?.id;
        if (state.runRequestId !== requestId || currentRunId !== runId) return null;

        const mutation = reset ? 'SET_CURRENT_RUN_ITEMS' : 'APPEND_CURRENT_RUN_ITEMS';
        const page = persistedRunItemPage(payload, 'The run items');
        commit(mutation, {
          items: page.items,
          cursor: page.nextCursor,
          total: page.totalCount,
        });
        return payload;
      } catch (err) {
        const problem = toRelationshipProblem(err, 'Run decisions could not be loaded.');
        if (state.runRequestId === requestId) commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        if (state.runRequestId === requestId) commit('SET_LOADING_RUN_ITEMS', false);
      }
    },

    async LOAD_ATTENTION_RUN_ITEMS({ commit }, { runId, cursor = null, pageSize = null }) {
      if (!runId) return null;
      commit('SET_PROBLEM', null);
      try {
        const payload = await relationshipService.getRunItems(runId, {
          cursor,
          classification: RELATIONSHIP_ATTENTION_CLASSIFICATION,
          pageSize,
        });
        return persistedRunItemPage(payload, 'The attention run items');
      } catch (err) {
        const problem = toRelationshipProblem(err, 'Attention items could not be loaded.');
        commit('SET_PROBLEM', problem);
        throw problem;
      }
    },

    async CONFIRM_CURRENT_RUN({ commit, dispatch, state }) {
      const run = state.currentRun;
      const runId = run?.id;
      const planHash = run?.planHash;
      if (!runId || !planHash) throw new Error('A persisted run and PlanHash are required.');

      commit('SET_CONFIRMING', true);
      commit('SET_PROBLEM', null);
      try {
        assertConfirmablePlan(state);
        requireGroupScopeFingerprints(state.currentRunItems);
        const purpose = state.currentRunApprovalPurpose;
        if (!['confirmRun', 'confirmCleanup'].includes(purpose)) {
          throw contractProblem('The run approval purpose is unavailable.', '/purpose');
        }
        const grant = await relationshipService.createApprovalGrant({
          purpose,
          runId,
          planHash,
        });

        const confirmed = await relationshipService.confirmRun(
          runId,
          { approvalGrantToken: approvalTokenFrom(grant), planHash },
          createRelationshipRequestKey('confirm'),
        );
        const updatedRun = { ...run, ...confirmed };
        commit('SET_CURRENT_RUN', updatedRun);
        return state.currentRun?.id === runId ? state.currentRun : updatedRun;
      } catch (err) {
        const problem = err?.detail
          ? err
          : toRelationshipProblem(err, 'The run could not be confirmed.');
        if (problem.code === 'ALREADY_CONFIRMED') {
          const winningRunId = problem.winningRunId || runId;
          try {
            const winningRun = await relationshipService.getRun(winningRunId);
            const currentRunId = state.currentRun?.id;
            if (currentRunId !== runId) return null;
            commit('SET_CURRENT_RUN', winningRun);
            commit('SET_PROBLEM', null);
            await dispatch('LOAD_MORE_CURRENT_RUN_ITEMS', { reset: true });
            return winningRun;
          } catch (winnerErr) {
            // Keep the canonical ALREADY_CONFIRMED problem visible when the winning run cannot be loaded.
          }
        }
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        commit('SET_CONFIRMING', false);
      }
    },

    async CANCEL_CURRENT_RUN({ commit, dispatch, state }) {
      const run = state.currentRun;
      const runId = run?.id;
      if (!runId || run?.status !== 'awaitingConfirmation') {
        throw new Error('An awaiting-confirmation run is required.');
      }

      commit('SET_CANCELLING', true);
      commit('SET_PROBLEM', null);
      try {
        const cancelled = await relationshipService.cancelRun(runId);
        if (cancelled?.runId !== runId || cancelled?.status !== 'cancelled') {
          throw contractProblem(
            'The server did not return the cancelled run state.',
            '/status',
          );
        }
        const updatedRun = {
          ...run,
          ...cancelled,
          id: runId,
        };
        commit('SET_CURRENT_RUN', updatedRun);
        return state.currentRun?.id === runId ? state.currentRun : updatedRun;
      } catch (err) {
        const problem = err?.detail
          ? err
          : toRelationshipProblem(err, 'The preview could not be cancelled.');
        if (problem.code === 'RUN_NOT_CANCELABLE') {
          try {
            await dispatch('REFRESH_CURRENT_RUN');
          } catch (refreshErr) {
            // Preserve the cancellation refusal when the authoritative run state cannot be reloaded.
          }
        }
        commit('SET_PROBLEM', problem);
        throw problem;
      } finally {
        commit('SET_CANCELLING', false);
      }
    },

    async REFRESH_CURRENT_RUN({ dispatch, state }, { signal = null, timeout = null } = {}) {
      const runId = state.currentRun?.id;
      if (!runId) return null;
      const requestId = state.runRequestId;
      const run = await dispatch('LOAD_RUN_STATUS', { runId, signal, timeout });
      if (signal?.aborted || !run) return null;
      const currentRunId = state.currentRun?.id;
      if (state.runRequestId !== requestId || currentRunId !== runId) return null;
      const acceptedRun = state.currentRun;
      if (RELATIONSHIP_RUN_TERMINAL_STATUSES.includes(acceptedRun?.status)) {
        dispatch('LOAD_MORE_CURRENT_RUN_ITEMS', { reset: true }).catch(() => {
          // The terminal status remains authoritative when its detail page needs a manual retry.
        });
      }
      return acceptedRun;
    },

    CLEAR_CURRENT_RUN({ commit }) {
      commit('SET_RUN_REQUEST_ID', ++runRequestSequence);
      commit('SET_CURRENT_RUN', null);
      commit('SET_CURRENT_RUN_MODE', RELATIONSHIP_PREVIEW_MODES.RESOLVE);
      commit('SET_CURRENT_RUN_APPROVAL_PURPOSE', null);
      commit('SET_CURRENT_RUN_ITEMS', { items: [], cursor: null, total: null });
      commit('SET_PREVIEWING', false);
      commit('SET_LOADING_RUN_ITEMS', false);
      commit('SET_PROBLEM', null);
    },
  },
};
